import React, { useEffect, useState } from 'react'
import type { ProbeResult } from '../lib/api'
import { kindOf } from '../lib/api'

interface HeaderProps {
  lastUpdate: number
  probes: ProbeResult[]
  refreshing: boolean
  notice: string
}

export function Header({ lastUpdate, probes, refreshing, notice }: HeaderProps) {
  const [clock, setClock] = useState('')
  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString())
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [])

  const up = probes.filter((p) => kindOf(p) === 'up').length
  const gated = probes.filter((p) => kindOf(p) === 'gated').length
  const down = probes.filter((p) => kindOf(p) === 'down').length
  const total = probes.length
  const attention = down + gated
  const allGood = total > 0 && attention === 0
  const formatTime = (ts: number) => (ts ? new Date(ts).toLocaleTimeString() : '—')

  return (
    <header className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-amber-400 text-glow-amber">DEMIGGWP · EMPIRE</div>
          <h1 className="font-sans text-3xl sm:text-5xl font-bold tracking-tight mt-1">CONTROL CENTRE</h1>
          <p className="text-xs text-zinc-500 mt-2">Live public edge · Cloudflare vantage · {total || '—'} endpoints</p>
        </div>
        <div className="text-right font-mono">
          <div className="text-2xl text-zinc-200 tabular-nums">{clock || '—'}</div>
          <div className="text-[10px] text-zinc-500 mt-1 flex items-center justify-end gap-2">
            <span className={`w-2 h-2 rounded-full ${refreshing ? 'bg-amber-400' : 'bg-[#2ee6c0]'} animate-pulse-glow`} />
            {refreshing ? 'SWEEPING' : 'LIVE'} · {formatTime(lastUpdate)}
          </div>
        </div>
      </div>

      <div className={`glass-panel rounded-md px-5 py-4 border ${allGood ? 'border-[#2ee6c0]/40' : down ? 'border-[#f43f5e]/40' : 'border-amber-400/40'}`}>
        <div className={`text-lg sm:text-2xl font-sans font-bold ${allGood ? 'text-[#2ee6c0] text-glow-cyan' : down ? 'text-[#f43f5e]' : 'text-amber-400 text-glow-amber'}`}>
          {total === 0 ? 'AWAITING FIRST SWEEP' : allGood ? 'ALL SYSTEMS OPERATIONAL' : `${attention} NEED ATTENTION`}
        </div>
        <div className="text-xs text-zinc-500 mt-1">{notice || 'Edge probes every 30s. LAN hosts are catalogued, not faked.'}</div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="UP" value={up} hint={`${total} probed`} color="text-[#2ee6c0]" />
        <Stat label="GATED" value={gated} hint="Access login" color="text-amber-400" />
        <Stat label="DOWN" value={down} hint="5xx / origin" color="text-[#f43f5e]" />
      </div>
    </header>
  )
}

function Stat({ label, value, hint, color }: { label: string; value: number; hint: string; color: string }) {
  return (
    <div className="glass-panel rounded-md px-4 py-4">
      <div className="text-[10px] tracking-[0.25em] text-zinc-500">{label}</div>
      <div className={`text-3xl sm:text-4xl mt-1 ${color}`}>{value}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>
    </div>
  )
}
